import { CheckInBookingOverviewDto } from '../../CheckInEntities/CheckInBookingOverviewDto';
import { BookingEntity } from 'Models/Entities';
import { transformTrailerSpace } from '../TableTransformHelpers/TrailerTransformers';
import { transformRego, transformVehicleSpace } from '../TableTransformHelpers/VehicleTransformers';
import { transformFreightCategory, transformFreightQuantity } from '../TableTransformHelpers/FreightTransformers';
import { transformCompanyName, transformDestination } from '../TableTransformHelpers/SharedTransformers';

export enum CheckInSorter {
	Default = 'Default',
	Fullname = 'Fullname',
	FullnameDesc = 'FullnameDesc',

	Destination = 'Destination',
	DestinationDesc = 'DestinationDesc',

	Company = 'Company',
	CompanyDesc = 'CompanyDesc',

	// Vehicle only sorters
	Rego = 'Rego',
	Trailer = 'Trailer',
	TrailerDesc = 'TrailerDesc',
	Vehicle = 'Vehicle',
	VehicleDesc = 'VehicleHighest',
	VehicleType = 'VehicleType',
	VehicleTypeDesc = 'VehicleTypeDesc',

	// Freight only sorters
	ShipmentNo = 'ShipmentNo',
	ShipmentNoDesc = 'ShipmentNoDesc',
	FreightCategory = 'FreightCategory',
	FreightCategoryDesc = 'FreightCategoryDesc',
	Quantity = 'Quantity',
	QuantityDesc = 'QuantityDesc',
}

export type BookingSorter = (a: CheckInBookingOverviewDto, b: CheckInBookingOverviewDto) => number;

export function sortByFullName(descending: boolean = false): BookingSorter {
	return (a: BookingEntity | CheckInBookingOverviewDto, b: BookingEntity | CheckInBookingOverviewDto) => {
		// Convert to upper case so that it sorts based on letters, not characters
		// e.g. Kate and karlo appears in the same sub-section, otherwise there will be two groups,
		// one for lowercase, another for uppercase
		const fullnameA = (a?.getFullName(true) ?? '').toUpperCase();
		const fullnameB = (b?.getFullName(true) ?? '').toUpperCase();
		if (fullnameA > fullnameB) return descending ? -1 : 1;
		if (fullnameA < fullnameB) return descending ? 1 : -1;
		return 0;
	};
}

export function sortByVehicleType(descending: boolean = false): BookingSorter {
	return (a: BookingEntity | CheckInBookingOverviewDto, b: BookingEntity | CheckInBookingOverviewDto) => {
		// Convert to upper case so that it sorts based on letters, not characters
		// e.g. Toyota and toyota appears in the same sub-section, otherwise there will be two groups,
		// one for lowercase, another for uppercase
		// No `?? ''` fallback: a template literal is always a string.
		const vehicleA = `${a?.getDisplayVehicleMake()} ${a?.getDisplayVehicleModel()}`.toUpperCase();
		const vehicleB = `${b?.getDisplayVehicleMake()} ${b?.getDisplayVehicleModel()}`.toUpperCase();
		if (vehicleA > vehicleB) return descending ? -1 : 1;
		if (vehicleA < vehicleB) return descending ? 1 : -1;
		return 0;
	};
}

export function sortByRego(descending: boolean = false): BookingSorter {
	return (a: BookingEntity | CheckInBookingOverviewDto, b: BookingEntity | CheckInBookingOverviewDto) => {
		const fullnameA = (transformRego(a) ?? '').toUpperCase();
		const fullnameB = (transformRego(b) ?? '').toUpperCase();
		if (fullnameA > fullnameB) return descending ? -1 : 1;
		if (fullnameA < fullnameB) return descending ? 1 : -1;
		return 0;
	};
}

export function sortByTrailer(descending: boolean = false): BookingSorter {
	return (a: CheckInBookingOverviewDto, b: CheckInBookingOverviewDto) => {
		const fullnameA = transformTrailerSpace(a) ?? '';
		const fullnameB = transformTrailerSpace(b) ?? '';
		if (fullnameA > fullnameB) return descending ? -1 : 1;
		if (fullnameA < fullnameB) return descending ? 1 : -1;
		return 0;
	};
}

export function sortByCar(descending: boolean = false): BookingSorter {
	return (a: CheckInBookingOverviewDto, b: CheckInBookingOverviewDto) => {
		const fullnameA = transformVehicleSpace(a) ?? '';
		const fullnameB = transformVehicleSpace(b) ?? '';
		if (fullnameA > fullnameB) return descending ? -1 : 1;
		if (fullnameA < fullnameB) return descending ? 1 : -1;
		return 0;
	};
}

export function sortByShipmentNo(descending: boolean = false): BookingSorter {
	return (a: CheckInBookingOverviewDto, b: CheckInBookingOverviewDto) => {
		const shipmentNoA = a.humanReadableId;
		const shipmentNoB = b.humanReadableId;
		if (shipmentNoA > shipmentNoB) return descending ? -1 : 1;
		if (shipmentNoA < shipmentNoB) return descending ? 1 : -1;
		return 0;
	};
}

export function sortByFreightCategory(descending: boolean = false): BookingSorter {
	return (a: CheckInBookingOverviewDto, b: CheckInBookingOverviewDto) => {
		const freightCategoryA = transformFreightCategory(a) ?? '';
		const freightCategoryB = transformFreightCategory(b) ?? '';
		if (freightCategoryA > freightCategoryB) return descending ? -1 : 1;
		if (freightCategoryA < freightCategoryB) return descending ? 1 : -1;
		return 0;
	};
}

export function sortByFreightQuantity(descending: boolean = false): BookingSorter {
	return (a: CheckInBookingOverviewDto, b: CheckInBookingOverviewDto) => {
		const freightQuantityA = transformFreightQuantity(a) ?? '';
		const freightQuantityB = transformFreightQuantity(b) ?? '';
		if (freightQuantityA > freightQuantityB) return descending ? -1 : 1;
		if (freightQuantityA < freightQuantityB) return descending ? 1 : -1;
		return 0;
	};
}

export function sortByCompanyName(descending: boolean = false): BookingSorter {
	return (a: CheckInBookingOverviewDto, b: CheckInBookingOverviewDto) => {
		// Convert to upper case so casing doesn't split otherwise-matching companies into
		// separate sub-sections.
		const companyA = transformCompanyName(a).toUpperCase();
		const companyB = transformCompanyName(b).toUpperCase();
		if (companyA > companyB) return descending ? -1 : 1;
		if (companyA < companyB) return descending ? 1 : -1;
		return 0;
	};
}

export function sortByDestination(descending: boolean = false): BookingSorter {
	return (a: CheckInBookingOverviewDto, b: CheckInBookingOverviewDto) => {
		const destinationA = transformDestination(a) ?? '';
		const destinationB = transformDestination(b) ?? '';
		if (destinationA > destinationB) return descending ? -1 : 1;
		if (destinationA < destinationB) return descending ? 1 : -1;
		return 0;
	};
}
